import { useQueryClient } from 'react-query';
import { useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  styled,
  Tooltip,
} from '@mui/material';
import {
  CompanySSOConnectionDetails,
  CompanySSOSettings,
  CompanySSOSettingsForm,
  SsoUserGroup,
} from './edit-company-sso.types';
import { useUpdateSSOSettings } from './use-update-sso-settings';
import { useGetSSOSettings } from './use-get-sso-settings';
import { SsoAttributeMapping } from './sso-attribute-mapping';
import { SsoConnectionCopyFields } from './sso-connection-copy-fields';
import { SsoSamlConfiguration } from './sso-saml-configuration';
import {
  FormCheckbox,
  Header,
  LwButton,
  Page,
  Toolbox,
  useFormButtons,
} from '../../../../../../../redesign';
import { paths } from '../../../../../../paths';
import { IconInfoRedesigned } from '../../../../../../../assets/img';
import { RenderUnsettledUI } from '../../../../../../../shared/components';
import { QUERY_KEYS } from '../../../../../../../api/query-keys';
import { useAlert } from '../../../../../../../shared/components/alerts';

const samlAttributeFieldName = {
  [SsoUserGroup.OGP]: 'ogpSamlAttribute',
  [SsoUserGroup.WORKER]: 'workerSamlAttribute',
} as const;

export const EditCompanySSOWrapper = () => {
  const companySSOQuery = useGetSSOSettings();

  if (companySSOQuery.status !== 'success') {
    return <RenderUnsettledUI data={companySSOQuery} />;
  }

  return (
    <EditCompanySSO
      ogpSsoEnabled={companySSOQuery.data.ogpSsoSettings.isEnabled ?? false}
      workerSsoEnabled={companySSOQuery.data.workerSsoSettings.isEnabled ?? false}
      ogpSamlAttribute={companySSOQuery.data.ogpSsoSettings.emailClaim ?? ''}
      workerSamlAttribute={companySSOQuery.data.workerSsoSettings.emailClaim ?? ''}
      ogpMetadataFileString={companySSOQuery.data.ogpSsoSettings.xmlData ?? null}
      workerMetadataFileString={companySSOQuery.data.workerSsoSettings.xmlData ?? null}
      ogpConnectionSettings={companySSOQuery.data.ogpSsoSettings.connectionDetails ?? null}
      workerConnectionSettings={companySSOQuery.data.workerSsoSettings.connectionDetails ?? null}
    />
  );
};

const EditCompanySSO = ({
  ogpSsoEnabled,
  workerSsoEnabled,
  ogpSamlAttribute,
  workerSamlAttribute,
  ogpMetadataFileString,
  workerMetadataFileString,
  ogpConnectionSettings,
  workerConnectionSettings,
}: CompanySSOSettings & {
  ogpSamlAttribute: string;
  workerSamlAttribute: string;
  ogpMetadataFileString: string;
  workerMetadataFileString: string;
  ogpConnectionSettings: CompanySSOConnectionDetails;
  workerConnectionSettings: CompanySSOConnectionDetails;
}) => {
  const queryClient = useQueryClient();
  const { ogpMetadataFile, workerMetadataFile } = useMemo(() => {
    const toFile = (xml: string | null | undefined, filename: string): File | null =>
      xml ? new File([xml], filename, { type: 'text/xml' }) : null;

    return {
      ogpMetadataFile: toFile(ogpMetadataFileString, 'ogp-metadata.xml'),
      workerMetadataFile: toFile(workerMetadataFileString, 'worker-metadata.xml'),
    };
  }, [ogpMetadataFileString, workerMetadataFileString]);
  const formMethods = useForm<CompanySSOSettingsForm>({
    mode: 'onTouched',
    defaultValues: {
      ogpSsoEnabled,
      workerSsoEnabled,
      ogpSamlAttribute,
      workerSamlAttribute,
      ogpMetadataFile,
      workerMetadataFile,
    },
  });
  const { alertSuccess, alertError } = useAlert();
  const { mutate: updateSsoSettings, status } = useUpdateSSOSettings();
  const { t } = useTranslation('ogp-screen-specific');
  const { submitButtonProps, cancelButtonProps, returnTo } = useFormButtons(status);

  const isOgpUserSSOEnabled = formMethods.watch('ogpSsoEnabled');
  const isWorkerSSOEnabled = formMethods.watch('workerSsoEnabled');
  const handleSubmit = useCallback(
    () =>
      formMethods.handleSubmit((data) => {
        if (data.ogpSsoEnabled && !data.ogpMetadataFile) {
          formMethods.setError('ogpMetadataFile', {
            type: 'manual',
            message: t('edit-sso-settings.file-drop.error.text'),
          });
          return;
        }
        if (data.workerSsoEnabled && !data.workerMetadataFile) {
          formMethods.setError('workerMetadataFile', {
            type: 'manual',
            message: t('edit-sso-settings.file-drop.error.text'),
          });
          return;
        }
        updateSsoSettings(data, {
          onSuccess: () => {
            queryClient.invalidateQueries(QUERY_KEYS.companies());
            alertSuccess(t('edit-sso-settings.success.message', { ns: 'ogp-screen-specific' }));
            returnTo(paths.Company, { search: '?tab=sso' })();
          },
          onError: (e) => {
            alertError(e);
          },
        });
      }),
    [formMethods]
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit()} noValidate>
        <Page
          header={<Header titleText={t('edit-sso-settings.header.title')} />}
          toolbox={
            <Toolbox underline>
              <LwButton
                iconColorMode="stroke"
                {...submitButtonProps}
                disabled={submitButtonProps.disabled}
              >
                {t('edit-sso-settings.save-sso-settings.label')}
              </LwButton>
              <LwButton
                iconColorMode="fill"
                onClick={returnTo(paths.Company, { search: '?tab=sso' })}
                {...cancelButtonProps}
              >
                Annuleer
              </LwButton>
            </Toolbox>
          }
        >
          <Box>
            <StyledAccordion
              expanded={isOgpUserSSOEnabled}
              slotProps={{ transition: { unmountOnExit: true } }}
            >
              <StyledAccordionSummary>
                <Grid container item xs={12} direction="row" alignItems={'center'}>
                  <Grid item>
                    <FormCheckbox
                      label={t('edit-sso-settings.enable-ogp-sso.label')}
                      name="ogpSsoEnabled"
                      control={formMethods.control}
                    />
                  </Grid>
                  <Grid item>
                    <Tooltip
                      describeChild
                      title={t('edit-sso-settings.enable-ogp-sso.tooltip')}
                      placement="top"
                    >
                      <span tabIndex={0} data-testid="ogp-tooltip">
                        <IconInfoRedesigned />
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
              </StyledAccordionSummary>
              <SSOFormFields
                ssoUserGroup={SsoUserGroup.OGP}
                formMethods={formMethods}
                connectionDetails={ogpConnectionSettings}
              />
            </StyledAccordion>
            <StyledAccordion
              expanded={isWorkerSSOEnabled}
              slotProps={{ transition: { unmountOnExit: true } }}
            >
              <StyledAccordionSummary>
                <Grid container item xs={12} direction="row" alignItems={'center'}>
                  <Grid item>
                    <FormCheckbox
                      label={t('edit-sso-settings.enable-worker-sso.label')}
                      name="workerSsoEnabled"
                      control={formMethods.control}
                    />
                  </Grid>
                  <Grid item>
                    <Tooltip
                      describeChild
                      title={t('edit-sso-settings.allow-worker-sso-features.tooltip')}
                      placement="top"
                    >
                      <span tabIndex={0} data-testid="worker-tooltip">
                        <IconInfoRedesigned />
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>
              </StyledAccordionSummary>
              <SSOFormFields
                ssoUserGroup={SsoUserGroup.WORKER}
                formMethods={formMethods}
                connectionDetails={workerConnectionSettings}
              />
            </StyledAccordion>
          </Box>
        </Page>
      </form>
    </FormProvider>
  );
};

const SSOFormFields = ({
  ssoUserGroup,
  formMethods,
  connectionDetails,
}: {
  ssoUserGroup: SsoUserGroup;
  formMethods: ReturnType<typeof useForm<CompanySSOSettingsForm>>;
  connectionDetails: CompanySSOConnectionDetails;
}) => {
  const { t } = useTranslation('ogp-screen-specific');
  const isUserGroupEnabled = !!formMethods.watch(
    `${ssoUserGroup.toLowerCase()}SsoEnabled` as keyof CompanySSOSettingsForm
  );
  return (
    <AccordionDetails>
      <Grid container spacing={2} xs={24}>
        <StyledPropertyGridItem item xs={12}>
          <SsoConnectionCopyFields
            connectionDetails={connectionDetails}
            entityIdLabel={t('edit-sso-settings.sso-entity-id.label')}
            acsUrlLabel={t('edit-sso-settings.sso-acs-url.label')}
            relayStateLabel={t('edit-sso-settings.sso-relay-state.label')}
          />
        </StyledPropertyGridItem>
        <StyledPropertyGridItem item xs={12}>
          <SsoAttributeMapping
            control={formMethods.control}
            samlAttributeName={samlAttributeFieldName[ssoUserGroup]}
            data-testid={`${ssoUserGroup.toLowerCase()}-sso-mapping`}
            required={isUserGroupEnabled}
          />
        </StyledPropertyGridItem>
        <StyledPropertyGridItem item xs={12}>
          <SsoSamlConfiguration
            ssoUserGroup={ssoUserGroup}
            watch={formMethods.watch}
            setValue={formMethods.setValue}
            clearErrors={formMethods.clearErrors}
            setError={formMethods.setError}
            hasError={
              !!formMethods.formState.errors[
                `${ssoUserGroup.toLowerCase()}MetadataFile` as keyof CompanySSOSettingsForm
              ]
            }
          />
        </StyledPropertyGridItem>
      </Grid>
    </AccordionDetails>
  );
};

const StyledAccordion = styled(Accordion)<{ expanded: boolean }>(({ theme }) => ({
  justifyContent: 'flex-start',
  boxShadow: 'none',
  '& .MuiAccordionSummary-expandIconWrapper': {},
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '&.MuiAccordion-root:before': {
    backgroundColor: theme.palette.background.paper,
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(() => ({
  paddingLeft: 0,
}));

const StyledPropertyGridItem = styled(Grid)(({ theme }) => ({
  gap: theme.spacing(2),
}));
